import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  GitCommit, Tag, Bug, Sparkles, Wrench, Clock, 
  ChevronDown, ChevronUp, Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const LOGO_URL = "/logo.png";

const changelog = [
  {
    version: '5.9.0',
    date: '2026-05-09',
    title: 'Planning unifié, guides interactifs et grosses optimisations',
    changes: [
      { type: 'feature', text: '🎯 Planning Drag & Drop : reprogrammer un RDV ou intervention en glissant-déposant directement dans la grille horaire' },
      { type: 'feature', text: '📄 Export PDF par membre : une page par collaborateur pour vos équipes terrain qui partent en intervention' },
      { type: 'feature', text: '🎨 Couleurs personnalisables par catégorie de services + étiquettes visuelles sur chaque événement du planning' },
      { type: 'feature', text: '✨ Suggestion automatique du meilleur collaborateur : quand un client choisit "Peu importe", l\'algorithme propose le membre le moins chargé' },
      { type: 'feature', text: '💼 Espace commercial dédié : les commerciaux gèrent leurs RDV (visio/physique) avec créneaux courts, validation/refus depuis leur dashboard' },
      { type: 'feature', text: '🖱️ Création depuis le planning : cliquez sur un créneau vide pour créer un RDV ou intervention en un clic' },
      { type: 'feature', text: '🎓 Guides interactifs sur chaque page : encadrés lumineux qui montrent les fonctionnalités, bouton "Revoir le guide" disponible partout' },
      { type: 'feature', text: '📋 Demandes centralisées : tous les devis, RDV et réservations à valider dans une vue unique avec filtres et auto-validation' },
      { type: 'improvement', text: '🌙 Refonte UI "Enterprise Command Center" : sidebar dark, palette professionnelle, fond contrasté' },
      { type: 'improvement', text: '📱 Améliorations responsive mobile + tablette sur Dashboard, Clients, Planning' },
      { type: 'improvement', text: '⚡ Performance : lazy loading sur 21 images + cache CDN 30 jours sur les uploads (avatars, photos, factures)' },
      { type: 'fix', text: '🐛 Logo cassé en production : helper centralisé `getAssetUrl` qui détecte automatiquement prod vs preview' },
    ]
  },
  {
    version: '5.7.2',
    date: '2026-04-28',
    title: 'Horaires par catégorie & Widgets Dispatcher',
    changes: [
      { type: 'feature', text: 'Horaires spécifiques par catégorie de service : configurez des plages horaires différentes pour Entretien (8h-18h), Aménagement (7h-16h), Conseil (9h-17h30)' },
      { type: 'feature', text: 'Widgets Dispatcher pour Élagage et Aménagement : le client choisit entre "Réserver une intervention" ou "Demander un devis"' },
      { type: 'improvement', text: 'Interface de configuration des catégories améliorée avec gestion des horaires' },
    ]
  },
  {
    version: '5.7.1',
    date: '2026-04-03',
    title: 'Configuration des créneaux dans les widgets',
    changes: [
      { type: 'feature', text: 'Configuration des créneaux dans les widgets : sélectionnez les types de créneaux à proposer (Matin, Après-midi, Journée, etc.) avec leurs prix' },
      { type: 'feature', text: 'Option "Demander le choix du créneau" : activez ou désactivez le choix du créneau par le client' },
      { type: 'fix', text: 'Samedis et dimanches sans horaires de travail affichent maintenant "Fermé" en rouge dans le planning' },
      { type: 'improvement', text: 'Affichage des prix fixes en vert et des prix calculés avec le détail du calcul' },
    ]
  },
  {
    version: '5.7.0',
    date: '2026-04-03',
    title: 'Prix fixe & Plages horaires pour les créneaux',
    changes: [
      { type: 'feature', text: 'Prix fixe pour types de créneaux : choisissez entre un prix calculé (taux horaire × durée) ou un prix fixe par prestation' },
      { type: 'feature', text: 'Plages horaires précises : définissez des horaires exacts pour chaque type de créneau (ex: Matin 08:00-12:00, Après-midi 12:15-16:15)' },
      { type: 'feature', text: 'Affichage enrichi dans le planning : le dropdown de réservation affiche maintenant le prix et les horaires de chaque type' },
      { type: 'feature', text: 'Bouton Modifier pour abonnements : le bouton "Modifier" est maintenant visible pour toutes les réservations confirmées' },
      { type: 'improvement', text: 'Interface de configuration des types de créneaux améliorée avec gestion des plages horaires' },
      { type: 'fix', text: 'Investigation bug vendredis : confirmé que les vendredis ne sont PAS bloqués dans le planning' },
    ]
  },
  {
    version: '5.6.0',
    date: '2026-03-31',
    title: 'Signature email personnalisable & Améliorations Planning',
    changes: [
      { type: 'feature', text: 'Signature email personnalisable : configurez des coordonnées différentes pour la signature de vos emails (nom commercial, email, téléphone, adresse, site web, SIRET)' },
      { type: 'feature', text: 'Modification complète des réservations : cliquez sur une réservation pour modifier le client, la date, le type de créneau, les collaborateurs et les notes' },
      { type: 'feature', text: 'Motif de décalage obligatoire : lors d\'un changement de date, un motif est requis (Météo, Indisponibilité, Demande client, etc.) et enregistré dans l\'historique' },
      { type: 'feature', text: 'Types de créneaux personnalisés : créez des créneaux avec nombre de collaborateurs requis (ex: "Journée 2 personnes")' },
      { type: 'feature', text: 'Widgets mis à jour : les types de créneaux personnalisés sont affichés avec calcul automatique du prix' },
      { type: 'fix', text: 'Correction sauvegarde des paramètres : les données de signature et le logo sont maintenant correctement persistés' },
      { type: 'fix', text: 'Correction calendrier widgets : les jours de fermeture (samedi/dimanche) sont correctement grisés' },
      { type: 'improvement', text: 'Logo mis à jour instantanément après upload sans rechargement de page' },
    ]
  },
  {
    version: '5.5.0',
    date: '2026-03-31',
    title: 'Création rapide de client & Paramètres unifiés',
    changes: [
      { type: 'feature', text: 'Création rapide de client : bouton "+" dans le sélecteur de clients pour créer un client sans quitter le flux de réservation' },
      { type: 'feature', text: 'Génération automatique de mot de passe pour les nouveaux clients' },
      { type: 'feature', text: 'Refonte de la page Paramètres : séparation claire entre "Mon Compte" (profil utilisateur) et "Entreprise" (coordonnées commerciales)' },
      { type: 'feature', text: 'Nouveau champ "Email commercial" pour distinguer l\'email de connexion de l\'email affiché aux clients' },
      { type: 'improvement', text: 'Suppression des menus en double dans la navigation (Paramètres)' },
      { type: 'fix', text: 'Correction : la signature email se met à jour correctement depuis les paramètres' },
    ]
  },
  {
    version: '5.4.0',
    date: '2026-03-30',
    title: 'Gestion complète des congés',
    changes: [
      { type: 'feature', text: 'Demandes de congés collaborateur : les employés peuvent demander leurs congés depuis leur espace' },
      { type: 'feature', text: 'Validation des congés : l\'entreprise peut approuver ou refuser les demandes avec commentaire' },
      { type: 'feature', text: 'Notifications automatiques : le collaborateur est notifié de la décision' },
      { type: 'feature', text: 'Import/Export CSV des clients : importez vos clients depuis un fichier CSV' },
      { type: 'feature', text: 'Page "Congés & Fermetures" : gérez les jours fériés et vacances de l\'entreprise' },
      { type: 'feature', text: 'Visualisation des congés dans le Planning : les jours de congés sont affichés avec l\'icône palmier' },
      { type: 'feature', text: 'Page "Historique des Congés" : consultez et exportez l\'historique des absences (PDF/CSV)' },
      { type: 'feature', text: 'Statistiques des congés : répartition par type, par collaborateur, moyenne par personne' },
      { type: 'feature', text: 'Option "Sur devis uniquement" pour les services : n\'apparaissent que dans les widgets RDV' },
    ]
  },
  {
    version: '5.3.0',
    date: '2026-03-30',
    title: 'Planning amélioré & Notifications client',
    changes: [
      { type: 'feature', text: 'Notifications client : le client reçoit une notification in-app ET un email quand sa demande est modifiée' },
      { type: 'feature', text: 'Planning "En attente" : les demandes pending sont visibles et modifiables (assigner un membre, changer date/heure)' },
      { type: 'feature', text: 'Horaires de travail dynamiques : les créneaux matin/après-midi dans les widgets correspondent aux vrais horaires configurés' },
      { type: 'fix', text: 'Correction : les collaborateurs peuvent maintenant démarrer/terminer une prestation (erreur "Accès non autorisé")' },
      { type: 'fix', text: 'Correction : les collaborateurs commerciaux n\'apparaissent que dans les widgets RDV' },
      { type: 'improvement', text: 'Cloche de notifications ajoutée dans l\'espace client' },
      { type: 'improvement', text: 'Création automatique du client depuis la modal de demande si non trouvé dans le CRM' },
    ]
  },
  {
    version: '5.2.0',
    date: '2026-03-28',
    title: 'Espace Client & Validation des interventions',
    changes: [
      { type: 'feature', text: 'Espace Client "Mon Abonnement" : suivi des passages (X/Y réalisés), modification des dates' },
      { type: 'feature', text: 'Espace Client "Validations" : consultation photos avant/après, validation ou contestation' },
      { type: 'feature', text: 'Règle des 72H : le client ne peut modifier les dates que 72H avant l\'intervention' },
      { type: 'feature', text: 'Support SMTP SSL/TLS : port 465 avec certificat SSL + port 587 avec STARTTLS' },
      { type: 'fix', text: 'Correction : les collaborateurs commerciaux n\'apparaissent que dans les widgets RDV (exclus des ponctuels/abonnements)' },
      { type: 'improvement', text: 'Navigation améliorée de l\'espace client avec 7 onglets dédiés' },
    ]
  },
  {
    version: '5.1.0',
    date: '2026-03-27',
    title: 'Planning avancé & Preuves d\'intervention',
    changes: [
      { type: 'feature', text: 'Boutons "Démarrer" et "Terminer" avec photos avant/après' },
      { type: 'feature', text: 'Compression automatique des photos en WebP (80% qualité, max 1600px)' },
      { type: 'feature', text: 'Statut "En cours" (cyan) dans les calendriers et planning' },
      { type: 'feature', text: 'Page Planning complète pour les collaborateurs avec vue calendrier' },
      { type: 'feature', text: 'Validation d\'intervention par l\'entreprise au nom du client avec note obligatoire' },
      { type: 'feature', text: 'Onglet "Mon Abonnement" côté entreprise : suivi X/Y passages, ajout/modification dates' },
      { type: 'improvement', text: 'Aperçu et téléchargement des factures dans le détail d\'intervention' },
    ]
  },
  {
    version: '4.8.1',
    date: '2026-02-17',
    title: 'Limite essai & Documentation',
    changes: [
      { type: 'feature', text: 'Limite de 3 clients pendant la période d\'essai gratuit' },
      { type: 'feature', text: 'Documentation complète avec 16 articles détaillés' },
      { type: 'improvement', text: 'Mise en avant de la page de connexion dédiée aux clients' },
      { type: 'improvement', text: 'URL personnalisée visible : /connexion/votre-entreprise' },
    ]
  },
  {
    version: '4.8.0',
    date: '2026-02-17',
    title: 'Gestion des entreprises & Factures client',
    changes: [
      { type: 'feature', text: 'Interface complète de gestion des entreprises clientes (Admin)' },
      { type: 'feature', text: 'Modification des infos entreprise, licence et remises depuis l\'admin' },
      { type: 'feature', text: 'Onglet "Mes Factures" dans l\'espace entreprise avec téléchargement PDF' },
      { type: 'feature', text: 'Affichage du type et statut de licence dans la liste des entreprises' },
      { type: 'improvement', text: 'Modale de modification avec 3 onglets : Informations, Licence, Remise' },
    ]
  },
  {
    version: '4.7.0',
    date: '2026-02-17',
    title: 'Système de facturation complet',
    changes: [
      { type: 'feature', text: 'Section "Facturation" dans l\'admin avec liste et création de factures' },
      { type: 'feature', text: 'Génération automatique de factures PDF avec TVA 20%' },
      { type: 'feature', text: 'Paramètres de facturation : coordonnées, nomenclature, TVA' },
      { type: 'feature', text: 'Envoi automatique des factures par email' },
      { type: 'feature', text: 'Suppression de licences depuis l\'admin' },
    ]
  },
  {
    version: '4.6.0',
    date: '2026-02-14',
    title: 'Templates d\'emails brandés',
    changes: [
      { type: 'feature', text: 'Logo entreprise automatique dans les emails clients' },
      { type: 'feature', text: 'Signature style carte de visite avec coordonnées complètes' },
      { type: 'feature', text: 'Aperçu en temps réel des templates dans les paramètres' },
      { type: 'improvement', text: 'Tous les emails clients incluent le branding entreprise' },
    ]
  },
  {
    version: '4.5.0',
    date: '2026-02-14',
    title: 'Notifications in-app & Prix dynamiques',
    changes: [
      { type: 'feature', text: 'Système de notifications in-app avec badge compteur' },
      { type: 'feature', text: 'Notifications cliquables avec redirection vers la page concernée' },
      { type: 'feature', text: 'Prix dynamiques sur la page de tarification' },
      { type: 'fix', text: 'Correction du bug écran blanc lors du clic sur notification' },
      { type: 'improvement', text: 'Logo ResaWork agrandi dans le header' },
    ]
  },
  {
    version: '4.4.0',
    date: '2026-02-10',
    title: 'Connexion personnalisée par entreprise',
    changes: [
      { type: 'feature', text: 'Page de connexion personnalisée par entreprise (/connexion/slug)' },
      { type: 'feature', text: 'Lien "Mot de passe oublié" et page de réinitialisation' },
      { type: 'feature', text: 'Footer minimal sur les pages de connexion' },
      { type: 'improvement', text: 'Page 404 pour entreprise inexistante' },
    ]
  },
  {
    version: '4.3.0',
    date: '2026-02-05',
    title: 'Tarification dynamique des licences',
    changes: [
      { type: 'feature', text: 'Admin peut modifier les prix mensuels et annuels des licences' },
      { type: 'feature', text: 'Prix synchronisés automatiquement sur le site et espaces entreprise' },
      { type: 'improvement', text: 'Configuration des jours d\'essai depuis l\'admin' },
    ]
  },
  {
    version: '3.1.0',
    date: '2026-01-22',
    title: 'Tarification flexible des services',
    changes: [
      { type: 'feature', text: '3 modes de tarification par prestation : Taux horaire, Prix fixe, Informatif' },
      { type: 'feature', text: 'Sélection multiple de services dans le widget ponctuel' },
      { type: 'feature', text: 'Calcul automatique du prix selon les services sélectionnés' },
      { type: 'improvement', text: 'Affichage des badges tarifaires dans le widget' },
    ]
  },
  {
    version: '3.0.0',
    date: '2026-01-22',
    title: 'Interface Mobile Responsive',
    changes: [
      { type: 'feature', text: 'Navigation mobile avec menu hamburger pour tous les dashboards' },
      { type: 'feature', text: 'Réservation pour un client directement depuis le planning' },
      { type: 'feature', text: 'Éditeur de widget responsive (onglets adaptés mobile)' },
      { type: 'improvement', text: 'Layout adaptatif Desktop/Mobile sur toutes les pages' },
    ]
  },
  {
    version: '2.8.0',
    date: '2026-01-22',
    title: 'Gestion avancée des licences & Templates emails',
    changes: [
      { type: 'feature', text: 'Vue détaillée des licences avec onglets Informations/Actions/Paiements' },
      { type: 'feature', text: 'Prolongation période d\'essai et application de code promo' },
      { type: 'feature', text: 'Templates d\'emails personnalisables avec balises dynamiques' },
      { type: 'feature', text: 'Enregistrement et historique des paiements de licence' },
    ]
  },
  {
    version: '2.7.0',
    date: '2026-01-22',
    title: 'Création client depuis le back-office',
    changes: [
      { type: 'feature', text: 'Bouton "Créer un client" dans le CRM entreprise' },
      { type: 'feature', text: 'Formulaire complet avec option d\'abonnement intégré' },
      { type: 'feature', text: 'Générateur de mot de passe automatique' },
      { type: 'improvement', text: 'Navigation optimisée - suppression liens redondants' },
    ]
  },
  {
    version: '2.6.0',
    date: '2026-01-21',
    title: 'Éditeur de widget avancé',
    changes: [
      { type: 'feature', text: 'Tarification flexible : taux horaire unique ou prix par service' },
      { type: 'feature', text: 'Déplacement des champs entre les différentes étapes' },
      { type: 'feature', text: '6 onglets dans l\'éditeur : Général, Tarification, Champs, Étapes, CGV, Style' },
    ]
  },
  {
    version: '2.5.0',
    date: '2026-01-21',
    title: 'Fiche client enrichie',
    changes: [
      { type: 'feature', text: 'Historique complet des interventions par client' },
      { type: 'feature', text: 'Réservation pour un client depuis sa fiche CRM' },
      { type: 'feature', text: 'Dépôt et téléchargement de factures PDF' },
      { type: 'improvement', text: 'Badge "Réservé par vous" pour les créneaux entreprise' },
    ]
  },
  {
    version: '2.4.0',
    date: '2026-01-21',
    title: 'Personnalisation entreprise',
    changes: [
      { type: 'feature', text: 'Upload de logo entreprise (remplace URL)' },
      { type: 'feature', text: 'Configuration SMTP par entreprise' },
      { type: 'feature', text: 'Page paramètres complète avec 4 onglets' },
    ]
  },
  {
    version: '2.3.0',
    date: '2026-01-20',
    title: 'Système d\'avis & Export PDF',
    changes: [
      { type: 'feature', text: 'Lien vers Google My Business pour les avis clients' },
      { type: 'feature', text: 'Tableau de bord des avis côté entreprise' },
      { type: 'feature', text: 'Export PDF du planning équipe' },
    ]
  },
  {
    version: '2.2.0',
    date: '2026-01-20',
    title: 'Réservation multiple',
    changes: [
      { type: 'feature', text: 'Réservation de plusieurs créneaux en une seule fois' },
      { type: 'feature', text: 'Affichage du prix après crédit d\'impôt (50%)' },
      { type: 'improvement', text: 'Dashboard client entièrement refondu' },
    ]
  },
  {
    version: '2.1.0',
    date: '2026-01-19',
    title: 'Dashboard entreprise amélioré',
    changes: [
      { type: 'feature', text: 'Statistiques : nombre de clients, créneaux réservés/disponibles' },
      { type: 'feature', text: 'Graphique du CA mensuel avec répartition Client/URSSAF' },
      { type: 'feature', text: 'Filtrage des collaborateurs par service dans le widget' },
    ]
  },
  {
    version: '2.0.0',
    date: '2026-01-18',
    title: 'Widgets de réservation',
    changes: [
      { type: 'feature', text: 'Widget ponctuel pour réservations ponctuelles' },
      { type: 'feature', text: 'Widget abonnement pour clients récurrents' },
      { type: 'feature', text: 'Personnalisation couleurs et style des widgets' },
      { type: 'feature', text: 'Code iframe pour intégration WordPress' },
    ]
  },
  {
    version: '1.2.0',
    date: '2026-01-21',
    title: 'Landing Page & Onboarding',
    changes: [
      { type: 'feature', text: 'Ajout de la landing page avec présentation des fonctionnalités et tarifs' },
      { type: 'feature', text: 'Système d\'onboarding en 4 étapes pour les nouvelles entreprises' },
      { type: 'feature', text: 'Page de documentation et tutoriels' },
      { type: 'feature', text: 'Page de suivi des versions (changelog)' },
      { type: 'feature', text: 'Système de tickets pour le support' },
      { type: 'fix', text: 'Correction du bug d\'affichage "NaNh" dans l\'espace client' },
      { type: 'improvement', text: 'Nouvelle police Poppins pour une meilleure lisibilité' },
    ]
  },
  {
    version: '1.1.0',
    date: '2026-01-18',
    title: 'Planning Avancé & Widgets',
    changes: [
      { type: 'feature', text: 'Vue planning avancée avec vues jour/semaine/mois/agenda' },
      { type: 'feature', text: 'Widget de réservation ponctuelle' },
      { type: 'feature', text: 'Personnalisation complète des widgets (champs, étapes, CGV)' },
      { type: 'feature', text: 'Gestion des services avec tarif horaire ou prix fixe' },
      { type: 'feature', text: 'Page de personnalisation globale (admin)' },
    ]
  },
  {
    version: '1.0.0',
    date: '2026-01-15',
    title: 'Version initiale',
    changes: [
      { type: 'feature', text: 'Back-office Admin avec gestion des licences' },
      { type: 'feature', text: 'Back-office Entreprise avec gestion clients et planning' },
      { type: 'feature', text: 'Espace Client avec réservations et crédits' },
      { type: 'feature', text: 'Widget de réservation d\'abonnement' },
      { type: 'feature', text: 'Système d\'authentification JWT multi-rôles' },
      { type: 'feature', text: 'Calcul automatique des prix (URSSAF)' },
    ]
  },
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'feature': return <Sparkles className="w-4 h-4 text-green-500" />;
    case 'fix': return <Bug className="w-4 h-4 text-red-500" />;
    case 'improvement': return <Wrench className="w-4 h-4 text-blue-500" />;
    default: return <GitCommit className="w-4 h-4 text-muted-foreground" />;
  }
};

const getTypeBadge = (type) => {
  switch (type) {
    case 'feature': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Nouveauté</Badge>;
    case 'fix': return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Correction</Badge>;
    case 'improvement': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Amélioration</Badge>;
    default: return <Badge variant="secondary">Autre</Badge>;
  }
};

export default function ChangelogPage() {
  const [expandedVersions, setExpandedVersions] = useState(['5.9.0']);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleVersion = (version) => {
    if (expandedVersions.includes(version)) {
      setExpandedVersions(expandedVersions.filter(v => v !== version));
    } else {
      setExpandedVersions([...expandedVersions, version]);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ResaWork" className="h-8" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/docs" className="text-sm font-medium text-muted-foreground hover:text-foreground">Documentation</Link>
              <Link to="/changelog" className="text-sm font-medium text-primary">Changelog</Link>
              <Link to="/tickets" className="text-sm font-medium text-muted-foreground hover:text-foreground">Support</Link>
            </nav>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">Connexion</Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/onboarding">Démarrer</Link>
              </Button>
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge variant="secondary" className="mb-4">
            <Tag className="w-3.5 h-3.5 mr-1.5" />
            Changelog
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Suivi des versions
          </h1>
          <p className="text-lg text-muted-foreground">
            Toutes les évolutions, nouvelles fonctionnalités et corrections de bugs
          </p>
        </div>
      </section>

      {/* Changelog Timeline */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block" />

            {changelog.map((release, index) => (
              <div key={release.version} className="relative mb-8">
                {/* Timeline dot */}
                <div className="absolute left-6 w-4 h-4 bg-primary rounded-full border-4 border-background hidden md:block" style={{ top: '24px' }} />
                
                <Card className={`ml-0 md:ml-16 ${index === 0 ? 'border-primary' : ''}`}>
                  <CardHeader 
                    className="cursor-pointer"
                    onClick={() => toggleVersion(release.version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Badge variant={index === 0 ? 'default' : 'secondary'} className="text-sm">
                          v{release.version}
                        </Badge>
                        <CardTitle className="text-lg">{release.title}</CardTitle>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(release.date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                        {expandedVersions.includes(release.version) ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedVersions.includes(release.version) && (
                    <CardContent>
                      <ul className="space-y-3">
                        {release.changes.map((change, i) => (
                          <li key={i} className="flex items-start gap-3">
                            {getTypeIcon(change.type)}
                            <div className="flex-1">
                              <span className="text-sm">{change.text}</span>
                            </div>
                            {getTypeBadge(change.type)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  )}
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{changelog.length}</p>
              <p className="text-sm text-muted-foreground">Versions</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-500">
                {changelog.reduce((acc, r) => acc + r.changes.filter(c => c.type === 'feature').length, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Fonctionnalités</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-500">
                {changelog.reduce((acc, r) => acc + r.changes.filter(c => c.type === 'fix').length, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Corrections</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">
                {changelog.reduce((acc, r) => acc + r.changes.filter(c => c.type === 'improvement').length, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Améliorations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="container mx-auto max-w-6xl text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} ResaWork. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
